import React, { useState } from 'react';
import { MessageCircle, X, Send, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import useSettingsStore from '../store/useSettingsStore';
import API from '../lib/api';
import { toast } from 'sonner';
import { isValidLink } from '../lib/links';

const MESSENGERS = ['Telegram', 'WhatsApp', 'MAX', 'Звонок', 'Другое'];

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', messenger: 'Telegram', messenger_custom: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const settings = useSettingsStore(s => s.settings);
  const content = useSettingsStore(s => s.content);
  const fb = content?.feedback || {};
  const ph = content?.placeholders || {};
  const directTg = (settings?.contacts_info || []).find(m => isValidLink(m.telegram) || isValidLink(m.whatsapp));

  const validateName = (v) => {
    if (!v || v.trim().length < 2) return 'Введите настоящее имя';
    if (!/^[А-ЯЁа-яё][А-ЯЁа-яё\s\-]{0,49}$/.test(v.trim())) return 'Имя на русском языке';
    return '';
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length === 0) return '';
    let formatted = '+7';
    if (digits.length > 1) formatted += ` (${digits.slice(1, 4)}`;
    if (digits.length > 4) formatted += `) ${digits.slice(4, 7)}`;
    if (digits.length > 7) formatted += `-${digits.slice(7, 9)}`;
    if (digits.length > 9) formatted += `-${digits.slice(9, 11)}`;
    return formatted;
  };

  const validatePhone = (v) => {
    const digits = v.replace(/\D/g, '');
    if (digits.length !== 11) return 'Введите полный номер';
    if (new Set(digits.slice(1)).size === 1) return 'Некорректный номер';
    return '';
  };

  const handleChange = (field, value) => {
    if (field === 'phone') value = formatPhone(value);
    setForm(f => ({ ...f, [field]: value }));
    if (field === 'name' && value) setErrors(e => ({ ...e, name: validateName(value) }));
    if (field === 'phone' && value.replace(/\D/g, '').length >= 11) setErrors(e => ({ ...e, phone: validatePhone(value) }));
  };

  const isValid = !validateName(form.name) && !validatePhone(form.phone);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nameErr = validateName(form.name);
    const phoneErr = validatePhone(form.phone);
    if (nameErr || phoneErr) { setErrors({ name: nameErr, phone: phoneErr }); return; }
    setSending(true);
    try {
      await API.post('/feedback', form);
      setSuccess(true);
      toast.success(fb.success_text || 'Спасибо! Мы свяжемся с вами.');
      setTimeout(() => { setOpen(false); setSuccess(false); setForm({ name: '', phone: '', messenger: 'Telegram', messenger_custom: '', message: '' }); }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Ошибка отправки');
    } finally { setSending(false); }
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="feedback-floating-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[9999] w-14 h-14 rounded-full bg-[var(--ap-primary)] text-white flex items-center justify-center shadow-lg animate-pulse-glow transition-transform hover:scale-110 active:scale-95"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-20 right-4 md:bottom-24 md:right-6 z-[9998] w-[calc(100vw-2rem)] max-w-sm"
          >
            <div className="glass rounded-2xl p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
              {success ? (
                <div className="text-center py-8" data-testid="feedback-success">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <Check size={32} className="text-green-500" />
                  </div>
                  <p className="text-sm text-[var(--ap-text-muted)]">{fb.success_text || 'Мы свяжемся с вами!'}</p>
                </div>
              ) : (
                <>

                  {directTg && (<>

                  <div className="flex gap-3 mb-4">
                    {isValidLink(directTg.telegram) && (
                      <a href={directTg.telegram.trim()} target="_blank" rel="noopener noreferrer" data-testid="feedback-tg-btn"
                        className="flex-1 py-3 rounded-lg bg-[#0088cc]/20 border border-[#0088cc]/30 text-center text-sm font-medium hover:bg-[#0088cc]/30 transition-colors">
                        {content?.contacts?.tg_button || 'Telegram'}
                      </a>
                    )}
                    {isValidLink(directTg.whatsapp) && (
                      <a href={directTg.whatsapp.trim()} target="_blank" rel="noopener noreferrer" data-testid="feedback-wa-btn"
                        className="flex-1 py-3 rounded-lg bg-[#25D366]/20 border border-[#25D366]/30 text-center text-sm font-medium hover:bg-[#25D366]/30 transition-colors">
                        {content?.contacts?.wa_button || 'WhatsApp'}
                      </a>
                    )}
                  </div>


                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-xs text-[var(--ap-text-muted)]">{fb.separator || 'или'}</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  </>)}

                  {/* Form */}
                  <h4 className="text-sm font-medium mb-3">{fb.form_title || 'Заявка на обратную связь'}</h4>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                      <input
                        data-testid="feedback-name-input"
                        value={form.name} onChange={e => handleChange('name', e.target.value)}
                        placeholder={ph.name || 'Введите ваше имя'}
                        className={`input-field text-sm ${errors.name ? 'input-error' : form.name && !validateName(form.name) ? 'input-success' : ''}`}
                      />
                      {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <input
                        data-testid="feedback-phone-input"
                        type="tel" value={form.phone} onChange={e => handleChange('phone', e.target.value)}
                        placeholder={ph.phone || '+7 (___) ___-__-__'}
                        className={`input-field text-sm ${errors.phone ? 'input-error' : form.phone.replace(/\D/g, '').length === 11 && !validatePhone(form.phone) ? 'input-success' : ''}`}
                      />
                      {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone}</p>}
                    </div>
                    <div>
                      <div className="flex flex-wrap gap-2">
                        {MESSENGERS.map(m => (
                          <button type="button" key={m} onClick={() => setForm(f => ({ ...f, messenger: m }))}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${form.messenger === m ? 'bg-[var(--ap-primary)] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}>
                            {m}
                          </button>
                        ))}
                      </div>
                      {form.messenger === 'Другое' && (
                        <input value={form.messenger_custom} onChange={e => setForm(f => ({ ...f, messenger_custom: e.target.value }))}
                          placeholder="Укажите способ связи" className="input-field text-sm mt-2" />
                      )}
                    </div>
                    <textarea
                      data-testid="feedback-message-input"
                      value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      placeholder={ph.message || 'Ваше сообщение...'} rows={3}
                      className="input-field text-sm resize-none"
                    />
                    <button
                      type="submit" disabled={!isValid || sending}
                      data-testid="feedback-submit-btn"
                      className="w-full py-3 rounded-lg bg-[var(--ap-primary)] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                      <Send size={16} />
                      {sending ? 'Отправка...' : fb.submit_button || 'Отправить заявку'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
